/**
 * Ditado por voz com CONTROLE MANUAL: um clique começa, outro termina.
 *
 * O DEFEITO QUE ISTO CORRIGE
 *
 * A versão anterior usava `continuous = false` e encerrava o ditado no
 * `onend` — e o `onend` da Web Speech API dispara na PRIMEIRA PAUSA de fala. Na
 * prática, o microfone morria depois de alguns segundos, no meio da frase, e
 * quem falava pausado nunca conseguia terminar um pedido. Pior: o encerramento
 * parecia aleatório, porque dependia do ritmo da fala.
 *
 * Silêncio não é comando de parada. Quem dita pensa, respira e consulta um
 * papel; tratar pausa como fim é confundir a pessoa pensando com a pessoa
 * terminando.
 *
 * COMO FUNCIONA AGORA
 *
 * `continuous = true` mantém o reconhecimento vivo através das pausas. Ainda
 * assim, o navegador encerra a sessão sozinho de tempos em tempos — limite dele,
 * não nosso —, e por isso o `onend` RECOMEÇA o reconhecimento enquanto a pessoa
 * não tiver clicado em parar. O texto acumulado sobrevive aos reinícios, e para
 * quem está falando nada acontece.
 *
 * Só o segundo clique encerra de verdade. É o que a intenção explícita da pessoa
 * significa, e é a única coisa que pode significá-lo.
 *
 * A MÁQUINA DE ESTADOS É EXPLÍCITA
 *
 * `idle → pedindo_permissao → gravando → parando → pronto`, com `erro` e
 * `permissao_negada` como saídas. Estados nomeados evitam a classe inteira de
 * defeitos em que um clique duplo, um evento atrasado de uma gravação anterior
 * ou um rerender da conversa derrubam o microfone — coisas que num `boolean`
 * ficam indistinguíveis.
 *
 * O QUE SOBREVIVE A RERENDER
 *
 * O reconhecedor, o texto acumulado e a intenção de gravar vivem em `ref`. Uma
 * resposta do assistente chegando no meio do ditado re-renderiza a conversa
 * inteira; se qualquer um deles estivesse em `state`, a gravação morreria junto.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type EstadoDaVoz =
  | 'INDISPONIVEL'
  | 'PARADO'
  | 'PEDINDO_PERMISSAO'
  | 'OUVINDO'
  | 'PARANDO'
  | 'PERMISSAO_NEGADA'
  | 'ERRO';

interface RetornoDaVoz {
  suportado: boolean;
  estado: EstadoDaVoz;
  /** Texto reconhecido até agora, com o trecho ainda provisório. */
  parcial: string;
  /** Segundos decorridos desde o início. Para o indicador na tela. */
  segundos: number;
  mensagemDeErro: string | null;
  iniciar: () => void;
  parar: () => void;
  /** Descarta o que foi ditado, sem entregar nada. */
  cancelar: () => void;
}

interface EventoDeReconhecimento {
  resultIndex: number;
  results: { isFinal: boolean; 0: { transcript: string } }[] & { length: number };
}

interface Reconhecedor {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: EventoDeReconhecimento) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

function construtorDeReconhecimento(): (new () => Reconhecedor) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => Reconhecedor;
    webkitSpeechRecognition?: new () => Reconhecedor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useVoz(aoConcluir: (texto: string) => void): RetornoDaVoz {
  const Construtor = construtorDeReconhecimento();
  const suportado = Construtor !== null;

  const [estado, setEstado] = useState<EstadoDaVoz>(suportado ? 'PARADO' : 'INDISPONIVEL');
  const [parcial, setParcial] = useState('');
  const [segundos, setSegundos] = useState(0);
  const [mensagemDeErro, setMensagemDeErro] = useState<string | null>(null);

  const refReconhecedor = useRef<Reconhecedor | null>(null);
  /** Texto FINALIZADO acumulado. Sobrevive aos reinícios do navegador. */
  const refTexto = useRef('');
  /**
   * A pessoa ainda quer gravar?
   *
   * É esta ref que distingue "o navegador encerrou a sessão sozinho" de "a
   * pessoa clicou em parar". Sem ela, os dois casos chegariam ao `onend`
   * idênticos — e foi por não distingui-los que a gravação morria na pausa.
   */
  const refQuerGravar = useRef(false);
  const refCancelado = useRef(false);
  const refInicio = useRef(0);
  const refCallback = useRef(aoConcluir);
  refCallback.current = aoConcluir;

  /** Cronômetro do indicador. Vive fora do reconhecedor, e não o controla. */
  useEffect(() => {
    if (estado !== 'OUVINDO') return undefined;
    const id = window.setInterval(() => {
      setSegundos(Math.floor((Date.now() - refInicio.current) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [estado]);

  const encerrarDeVez = useCallback((entregar: boolean): void => {
    const texto = refTexto.current.trim();
    refQuerGravar.current = false;
    refReconhecedor.current = null;
    setParcial('');
    setEstado('PARADO');
    // O texto vai para o CAMPO, nunca direto para o envio: reconhecimento erra
    // nome próprio, e confirmar uma ação construída sobre uma palavra que a
    // pessoa não disse é o pior desfecho possível.
    if (entregar && texto !== '') refCallback.current(texto);
    refTexto.current = '';
  }, []);

  const criarEIniciar = useCallback((): void => {
    if (Construtor === null) return;
    const r = new Construtor();
    r.lang = 'pt-BR';
    // A diferença que corrige o defeito: mantém a escuta através das pausas.
    r.continuous = true;
    r.interimResults = true;

    r.onstart = () => setEstado('OUVINDO');

    r.onresult = (e) => {
      // Evento atrasado de uma gravação já encerrada não pode contaminar a
      // próxima: se ninguém quer gravar, o resultado é descartado.
      if (!refQuerGravar.current) return;
      let finalizado = refTexto.current;
      let provisorio = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const trecho = e.results[i]!;
        if (trecho.isFinal) finalizado += `${trecho[0].transcript} `;
        else provisorio += trecho[0].transcript;
      }
      refTexto.current = finalizado;
      setParcial((finalizado + provisorio).replace(/\s+/g, ' ').trim());
    };

    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        refQuerGravar.current = false;
        setEstado('PERMISSAO_NEGADA');
        setMensagemDeErro(
          'O navegador bloqueou o microfone. Libere a permissão nas configurações do site, ou digite o pedido.',
        );
        return;
      }
      // `no-speech` e `aborted` NÃO encerram: a pessoa pode estar só pensando, e
      // quem manda parar é o segundo clique.
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      if (e.error === 'audio-capture') {
        refQuerGravar.current = false;
        setEstado('ERRO');
        setMensagemDeErro('Nenhum microfone disponível. Verifique o dispositivo, ou digite o pedido.');
        return;
      }
      setMensagemDeErro('O reconhecimento falhou por um instante; continuo ouvindo.');
    };

    r.onend = () => {
      // O navegador encerra a sessão sozinho de tempos em tempos. Enquanto a
      // pessoa não tiver pedido para parar, recomeçamos — para quem está falando,
      // nada acontece, e o texto acumulado continua de onde estava.
      if (refQuerGravar.current) {
        try {
          r.start();
          return;
        } catch {
          // Reinício recusado (o navegador ainda está fechando a sessão anterior):
          // tenta de novo no próximo tique, sem perder o que já foi dito.
          window.setTimeout(() => {
            if (refQuerGravar.current) criarEIniciar();
          }, 250);
          return;
        }
      }
      encerrarDeVez(!refCancelado.current);
      refCancelado.current = false;
    };

    refReconhecedor.current = r;
    try {
      r.start();
    } catch {
      // Já havia um reconhecimento em curso: o microfone está aberto, e é isso
      // que a pessoa queria.
      setEstado('OUVINDO');
    }
  }, [Construtor, encerrarDeVez]);

  const iniciar = useCallback((): void => {
    if (Construtor === null) return;
    // Clique duplo não abre duas gravações.
    if (refQuerGravar.current) return;
    setMensagemDeErro(null);
    setParcial('');
    setSegundos(0);
    refTexto.current = '';
    refCancelado.current = false;
    refQuerGravar.current = true;
    refInicio.current = Date.now();
    setEstado('PEDINDO_PERMISSAO');
    criarEIniciar();
  }, [Construtor, criarEIniciar]);

  const parar = useCallback((): void => {
    if (!refQuerGravar.current) return;
    refQuerGravar.current = false;
    setEstado('PARANDO');
    const r = refReconhecedor.current;
    if (r === null) {
      encerrarDeVez(true);
      return;
    }
    // `stop()` entrega o que faltava reconhecer e dispara `onend`, que agora
    // encerra de verdade porque ninguém quer mais gravar.
    try {
      r.stop();
    } catch {
      encerrarDeVez(true);
    }
  }, [encerrarDeVez]);

  const cancelar = useCallback((): void => {
    refCancelado.current = true;
    refQuerGravar.current = false;
    refTexto.current = '';
    setEstado('PARANDO');
    const r = refReconhecedor.current;
    try {
      r?.abort();
    } catch {
      /* já encerrado */
    }
    encerrarDeVez(false);
  }, [encerrarDeVez]);

  // Sair da tela corta o microfone. Sem isto, navegar durante o ditado deixaria
  // o indicador de gravação do navegador aceso sem nada explicando por quê — o
  // pior tipo de susto num sistema que ouve.
  useEffect(
    () => () => {
      refQuerGravar.current = false;
      try {
        refReconhecedor.current?.abort();
      } catch {
        /* nada a fazer no desmonte */
      }
    },
    [],
  );

  return { suportado, estado, parcial, segundos, mensagemDeErro, iniciar, parar, cancelar };
}
